// ABOUTME: What the discussion reads straight from the cache by id: the item's counters, which every answer of the
// ABOUTME: feed and its mutations updates, and the item itself — its day and whether the athlete sees it yet.
import type { ApolloCache, Reference } from "@apollo/client"
import { useFragment } from "@apollo/client/react"
import {
  DiscussionItemFragmentDoc,
  DiscussionSummaryFragmentDoc,
  type DiscussionItemFragment,
  type DiscussionSummaryFragment,
} from "@/graphql/main/gql/graphql"

/** The counters as the viewer sees them; null until something brought them into the cache. */
export function useDiscussionSummary(itemId: string): DiscussionSummaryFragment | null {
  const { data, complete } = useFragment({
    fragment: DiscussionSummaryFragmentDoc,
    from: { __typename: "CalendarItemDiscussion", calendarItemId: itemId },
  })
  return complete ? data : null
}

/** The item as the calendar loaded it; null when its window never did (a link opened cold). */
export function useDiscussionItem(itemId: string): DiscussionItemFragment | null {
  const { data, complete } = useFragment({
    fragment: DiscussionItemFragmentDoc,
    from: { __typename: "CalendarItem", id: itemId },
  })
  return complete ? data : null
}

/**
 * The newer of the counters an answer brings and those in the cache. Answers land in any order — a
 * page read before a read mark may answer after it — and the counters are a snapshot: of two, the
 * one behind on the read mark or the newest number was taken first, for neither ever goes back
 * (gql-api internal/repo/gen/query/discussions.sql:76, :287, :338-340). With the newest number level,
 * the one counting more messages was: a message is numbered when written and a deleted one never
 * comes back, so the count of live ones (:357) only falls while the newest number stands. Of two level
 * on all of these, the one answered last stands.
 */
export function newerSummary(cache: ApolloCache, incoming: DiscussionSummaryFragment): DiscussionSummaryFragment {
  const cached = cachedSummary(cache, incoming.calendarItemId)
  return cached && isOlder(incoming, cached) ? cached : incoming
}

const isOlder = (incoming: DiscussionSummaryFragment, cached: DiscussionSummaryFragment): boolean =>
  incoming.readSeq < cached.readSeq ||
  incoming.lastSeq < cached.lastSeq ||
  (incoming.lastSeq === cached.lastSeq && incoming.messageCount > cached.messageCount)

/** The counters the cache holds for the item; null when nothing brought them — it is not in the loaded window. */
export function cachedSummary(cache: ApolloCache, itemId: string): DiscussionSummaryFragment | null {
  return cache.readFragment({
    fragment: DiscussionSummaryFragmentDoc,
    id: cache.identify({ __typename: "CalendarItemDiscussion", calendarItemId: itemId }),
  })
}

/** Writes the counters an answer brings, unless the cache holds newer ones (newerSummary). */
export function writeNewerSummary(cache: ApolloCache, incoming: DiscussionSummaryFragment): void {
  if (newerSummary(cache, incoming) === incoming)
    cache.writeFragment({ fragment: DiscussionSummaryFragmentDoc, data: incoming })
  attachSummary(cache, incoming.calendarItemId)
}

/**
 * A card the calendar answered without counters — the count failed — gets the cached ones now. A write
 * of the counters reaches them alone, never the card's field that holds them: a fragment's writes only
 * the entity it names (cache.js:299-305), a feed page's only what it selects (writeToStore.js:165-180).
 * A card not in the cache stays out of it (entityStore.js:133-135).
 */
export function attachSummary(cache: ApolloCache, calendarItemId: string): void {
  cache.modify({
    id: cache.identify({ __typename: "CalendarItem", id: calendarItemId }),
    fields: {
      discussion: (existing: Reference | null, { toReference }) =>
        existing ?? toReference({ __typename: "CalendarItemDiscussion", calendarItemId }) ?? null,
    },
  })
}

const isSummary = (value: Record<string, unknown>): boolean =>
  value.__typename === "CalendarItemDiscussion" && typeof value.calendarItemId === "string"

/**
 * An answer, or what is about to be written, with each discussion summary older than the cached one taken
 * over by the cached one (newerSummary); the very same value when none is. Run it right before the write:
 * a mark answered between the check and the write would be undone all the same.
 */
export function keepNewerSummaries(cache: ApolloCache, value: unknown): unknown {
  if (Array.isArray(value)) {
    const next = value.map((element: unknown) => keepNewerSummaries(cache, element))
    return next.some((element, index) => element !== value[index]) ? next : value
  }
  if (value === null || typeof value !== "object") return value
  const record = value as Record<string, unknown>
  if (isSummary(record)) {
    const newer = newerSummary(cache, record as unknown as DiscussionSummaryFragment)
    return newer === record ? record : { ...record, ...newer }
  }
  let next: Record<string, unknown> | null = null
  for (const [key, child] of Object.entries(record)) {
    const kept = keepNewerSummaries(cache, child)
    if (kept === child) continue
    next ??= { ...record }
    next[key] = kept
  }
  return next ?? record
}
